import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from "react";
import { User } from "../types/permissions";
import { clearAntiforgeryToken } from "../auth/antiforgery";

interface AuthContextType {
  user: User | null;
  setUser: Dispatch<SetStateAction<User | null>>;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(() => {
    try {
      const storedUser = localStorage.getItem("moneylights-user");
      if (storedUser) {
        return JSON.parse(storedUser);
      }
    } catch {
      // ignore storage errors
    }
    return null;
  });
  const lastUserIdRef = useRef<string | null>(user?.id ?? null);

  useEffect(() => {
    const currentUserId = user?.id ?? null;
    if (lastUserIdRef.current !== currentUserId) {
      clearAntiforgeryToken();
      lastUserIdRef.current = currentUserId;
    }
  }, [user?.id]);

  useEffect(() => {
    try {
      if (user) {
        localStorage.setItem("moneylights-user", JSON.stringify(user));
      } else {
        localStorage.removeItem("moneylights-user");
      }
    } catch {
      // ignore storage errors
    }
  }, [user]);

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

/**
 * O utilizador para quem PODE viver fora do provider — devolve null em vez de
 * rebentar.
 *
 * Mesmo idioma do `useAgentIdentityValue`, do `useChatControlsValue` e do
 * `useLumaFocusValue`; e a razao repete-se: um componente de apresentacao
 * passou a querer o id do utilizador (o aviso de historico incompleto, para
 * separar por pessoa a escolha guardada no localStorage) e e renderizado em
 * testes que montam a vista sem AuthProvider. Com o hook estrito, sete testes
 * do Cashflow passaram a atirar «useAuth must be used within an AuthProvider».
 *
 * Quem PRECISA mesmo de autenticacao continua a usar o useAuth estrito — a
 * excepcao e para quem so quer saber quem e, se houver alguem.
 */
export function useAuthValue() {
  return useContext(AuthContext) ?? null;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
